import { useToast } from '@/hooks/use-toast'
import { addMeterSizeRate, getConnectionType } from '@/lib/api/meter'
import { ConnectionType } from '@/types/zone-connection-rates-type'
import { DropdownMenuSeparator } from '@radix-ui/react-dropdown-menu'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import React from 'react'
import { z } from 'zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { Card } from '../ui/card'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ComboboxSearch } from '../combobox-search'
import { Button } from '../ui/button'
import { Icons } from '../icons'

interface MeterSizeRateFormProps {
  onClose: () => void
  setShowModal: (showModal: boolean) => void
}

const schema = z.object({
  rate_type_id: z.number().min(1, "Please select a connection type"),
  min_volume: z.number().min(0, 'Minimum volume is required'),
  max_volume: z.number().nullable(),
  charge_per_cubic_meter: z.number().min(0, 'Charge per cubic meter is required'),
})

type RequestBodyForm = {
  rate_type_id: number
  min_volume: number
  max_volume: number | null
  charge_per_cubic_meter: number
}

export default function MeterSizeRateForm({
  onClose,
  setShowModal,
}: MeterSizeRateFormProps) {
  const { toast } = useToast()
  const { id } = useParams({ strict: false })
  const queryClient = useQueryClient()

  const { data: connectionTypeData } = useQuery({
    queryKey: ['connection_type'],
    queryFn: getConnectionType,

  })

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      rate_type_id: 0,
      min_volume: 1,
      max_volume: 10,
      charge_per_cubic_meter: 10,
    }
  })

  const mutate = useMutation({
    mutationFn: addMeterSizeRate,
  })
  
  const connectionTypes: Array<{
      value: string
      label: string
      id?: string | number
    }> =
      (connectionTypeData as ConnectionType[]) // Ensure data is an array
        ?.map((item) => ({
          value: item.type_name,
          label: item.type_name,
          id: item.id,
        }))
        .filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.value === item.value), // Ensure unique values
        ) ?? []

  const onSubmit = async (data: z.infer<typeof schema>) => {
    mutate.mutate(data)
  }

  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />

      <Form {...form}>
        <form
          className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Card className="my-4 grid-cols-2 gap-4 p-6 md:grid">
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="rate_type_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="py-1">
                      Connection type
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <ComboboxSearch
                        field={{
                          ...field,
                        }}
                        options={connectionTypes}
                        placeholder="Select a connection type"
                        idAsValue
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>

          <div className="col-span-2 flex w-full justify-end gap-3">
            <Button type="submit" className="w-28" disabled={mutate.isPending}>
              {mutate.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
          
        </form>
      </Form>
    </div>
  )
}
